import Fuse, { type FuseResult, type IFuseOptions } from "fuse.js";
import { useEffect, useState } from "react";
import { Highlighter, Menu, MenuItem } from "react-bootstrap-typeahead";
import type { RenderMenuProps } from "react-bootstrap-typeahead/types/components/Typeahead";
import {
  type Case,
  type CollectionItem,
  type ExternalClientIEnumerableExternalPartnerResponse,
  ExternalPartners,
} from "@/briefpoint-client";
import ClioLogo from "@/images/Glyph_Clio Glyph Blue.svg?react";
import BriefpointLogo from "@/images/logo.svg?react";
import MyCaseLogo from "@/images/MyCase.svg?react";
import SmokeballLogo from "@/images/Smokeball_logo_mark.svg?react";
import type {
  CaseResponse,
  ClientResponse,
  CombinedOption,
  JurisdictionResponse,
} from "@/lib/types";
import styles from "./ReviewPage.module.scss";
import { blankId } from "./SourceDocumentReview";

type TypeaheadState = {
  text: string;
};

function getCaseSourceLogo(
  showSourceLogos: boolean,
  partner?: ExternalPartners,
) {
  if (!showSourceLogos) {
    return null;
  }

  if (!partner) {
    return <BriefpointLogo title="Briefpoint Case" />;
  }

  switch (partner) {
    case ExternalPartners.Clio:
      return <ClioLogo title="Clio Case" />;
    case ExternalPartners.Smokeball:
      return <SmokeballLogo title="Smokeball Case" />;
    case ExternalPartners.MyCase:
      return <MyCaseLogo title="MyCase Case" />;
    default:
      return <BriefpointLogo title="Briefpoint Case" />;
  }
}

export function remapExternalClientList(
  res: ExternalClientIEnumerableExternalPartnerResponse,
): ClientResponse[] | undefined {
  return res.data?.map((res) => ({
    id: "", // ID is left empty to indicate this is remapped client
    contactFirstName: res.firstName,
    contactLastName: res.lastName,
    partner: res.partner,
    integration: { identifier: res.id, partner: res.partner },
    name: res.name,
    ...(res.email ? { contactEmail: res.email } : {}), // Add the external client's email if it exists
  }));
}

export function updateJurisdictionAndVenue(
  docJurisdiction: string | null | undefined,
  docVenue: string,
  // `Case` type is intentional, for creates without clientId, id
  caseData: Case | undefined,
  handleUpdateJurisdiction: (jxId: string) => void,
  setVenue: React.Dispatch<React.SetStateAction<string>>,
) {
  if (!docJurisdiction || docJurisdiction === blankId) return;

  const isCaseVenueOrJurisdictionBlank =
    caseData?.venue === blankId || caseData?.jurisdiction === blankId;

  if (!isCaseVenueOrJurisdictionBlank) return;

  const isJurisdictionMissing =
    caseData?.jurisdiction === blankId || !caseData?.jurisdiction;

  if (isJurisdictionMissing) {
    handleUpdateJurisdiction(docJurisdiction);
    const shouldUpdateVenue =
      docVenue && docVenue !== blankId && caseData?.venue === blankId;

    if (shouldUpdateVenue) {
      setVenue(docVenue);
    }
  }
}

export function CustomMenuClient({
  results,
  menuProps,
  state,
  showSourceLogos,
  isClientNew,
}: {
  results: CombinedOption[];
  menuProps: RenderMenuProps;
  state: { text: string };
  showSourceLogos: boolean;
  isClientNew: boolean;
}) {
  const processedResults = results.reduce(
    (acc: CombinedOption[], current: CombinedOption) => {
      const existingIndex = acc.findIndex((item) => item.name === current.name);

      if (existingIndex === -1) {
        acc.push(current);
      } else {
        const existing = acc[existingIndex];
        if (!existing.internalId && current.internalId) {
          acc[existingIndex] = current;
        }
      }

      return acc;
    },
    [],
  );

  return (
    <Menu {...menuProps}>
      {processedResults.map((option: CombinedOption, index: number) => {
        if (
          !isClientNew &&
          !option.partner &&
          !option.internalId &&
          !option.externalId
        )
          return null;

        return (
          <MenuItem key={index} option={option} position={index}>
            {option.internalId ? (
              <p className={styles.customMenuLabel}>
                {getCaseSourceLogo(showSourceLogos, option.partner)}
                <Highlighter search={state.text}>
                  {option.name || ""}
                </Highlighter>
              </p>
            ) : (
              <div>
                {!option.partner && (
                  <small>
                    <i>Suggested name for New Client:</i>
                  </small>
                )}
                <p className={styles.customMenuLabel}>
                  {option.partner &&
                    getCaseSourceLogo(showSourceLogos, option.partner)}
                  <Highlighter search={state.text}>
                    {option?.name || ""}
                  </Highlighter>
                </p>
              </div>
            )}
          </MenuItem>
        );
      })}
    </Menu>
  );
}

export function NoSuggestCustomMenuClient({
  results,
  menuProps,
  state,
  showSourceLogos,
}: {
  results: CombinedOption[];
  menuProps: RenderMenuProps;
  state: { text: string };
  showSourceLogos: boolean;
}) {
  return (
    <Menu {...menuProps}>
      {results.map((option, index) => (
        <MenuItem key={index} option={option} position={index}>
          <p className={styles.customMenuLabel}>
            {getCaseSourceLogo(showSourceLogos, option.integration?.partner)}
            <Highlighter search={state.text}>
              {`${option.customOption ? `Create client: ${state.text}` : option.name}`}
            </Highlighter>
          </p>
        </MenuItem>
      ))}
    </Menu>
  );
}

export function CustomMenu({
  results,
  menuProps,
  state,
  showSourceLogos,
}: {
  results: CombinedOption[];
  menuProps: RenderMenuProps;
  state: TypeaheadState;
  showSourceLogos: boolean;
}) {
  return (
    <Menu {...menuProps}>
      {results.map((option: CombinedOption, index: number) => (
        <MenuItem key={index} option={option} position={index}>
          {option.internalId || option.externalId ? (
            <p className={styles.customMenuLabel}>
              {getCaseSourceLogo(showSourceLogos, option.partner)}
              <Highlighter search={state.text}>{option.title}</Highlighter>
            </p>
          ) : (
            <div>
              <small>
                <i>Suggested name for New Case:</i>
              </small>
              <p className={styles.customMenuLabel}>
                {getCaseSourceLogo(showSourceLogos, option.partner)}
                <Highlighter search={state.text}>
                  {`${option.customOption ? option.label || "" : option.title}`}
                </Highlighter>
              </p>
            </div>
          )}
        </MenuItem>
      ))}
    </Menu>
  );
}

export function NoSuggestCustomMenu({
  results,
  menuProps,
  state,
  showSourceLogos,
}: {
  results: CaseResponse[];
  menuProps: RenderMenuProps;
  state: { text: string };
  showSourceLogos: boolean;
}) {
  return (
    <Menu {...menuProps}>
      {results.map((option: CaseResponse, index: number) => (
        <MenuItem key={index} option={option} position={index}>
          {option.integration?.partner ? (
            <p className={styles.customMenuLabel}>
              {getCaseSourceLogo(showSourceLogos, option.integration.partner)}
              <Highlighter search={state.text}>
                {`${option.shortTitle}`}
              </Highlighter>
            </p>
          ) : (
            <div>
              <p className={styles.customMenuLabel}>
                {getCaseSourceLogo(
                  showSourceLogos,
                  option.integration?.partner,
                )}
                <Highlighter search={state.text}>
                  {`${option?.shortTitle ?? ""}`}
                </Highlighter>
              </p>
            </div>
          )}
        </MenuItem>
      ))}
    </Menu>
  );
}

export function NoSuggestCustomMenuExternalUpload({
  results,
  menuProps,
  state,
  showSourceLogos,
}: {
  results: CombinedOption[];
  menuProps: RenderMenuProps;
  state: { text: string };
  showSourceLogos: boolean;
}) {
  return (
    <Menu {...menuProps}>
      {results.map((option: CombinedOption, index: number) => (
        <MenuItem key={index} option={option} position={index}>
          {option?.partner ? (
            <p className={styles.customMenuLabel}>
              {getCaseSourceLogo(showSourceLogos, option.partner)}
              <Highlighter search={state.text}>{option.title}</Highlighter>
            </p>
          ) : (
            <div>
              <p className={styles.customMenuLabel}>
                {getCaseSourceLogo(showSourceLogos, option?.partner)}
                <Highlighter search={state.text}>
                  {`${option.customOption ? `Create case: ${state.text}` : option.title}`}
                </Highlighter>
              </p>
            </div>
          )}
        </MenuItem>
      ))}
    </Menu>
  );
}

export default function usePositionActive(): [
  React.Dispatch<React.SetStateAction<HTMLDivElement | undefined>>,
  React.Dispatch<React.SetStateAction<HTMLSpanElement | undefined>>,
  React.Dispatch<React.SetStateAction<HTMLDivElement | undefined>>,
] {
  const [activeRef, setActiveRef] = useState<HTMLDivElement>();
  const [activeLineRef, setActiveLineRef] = useState<HTMLSpanElement>();
  const [informationRef, setInformationRef] = useState<HTMLDivElement>();

  useEffect(() => {
    function handleResize() {
      if (activeRef && activeLineRef) {
        const activeBoundingRect = activeRef.getBoundingClientRect();
        const activeLineBoundingRect = activeLineRef.getBoundingClientRect();
        activeLineRef.style.width = `${activeLineBoundingRect.right - activeBoundingRect.right}px`;
        if (informationRef) {
          const informationBoundingRect =
            informationRef.getBoundingClientRect();
          informationRef.style.top = `${activeLineBoundingRect.top - informationBoundingRect.height / 1.55}px`;
        }
      }
    }
    // Add event listener
    window.addEventListener("resize", handleResize);
    // Call handler right away so state gets updated with initial window size
    handleResize();
    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleResize);
  }, [activeRef, activeLineRef, informationRef]); // Empty array ensures that effect is only run on mount

  return [setActiveRef, setActiveLineRef, setInformationRef];
}

// PLEADING UTILITY FUNCTIONS

export const pleadCollectionRemove = (
  index: number,
  arr: CollectionItem[],
  stateFunc: React.Dispatch<React.SetStateAction<CollectionItem[] | null>>,
) => {
  if (arr !== null) {
    const newAllegations: CollectionItem[] = arr.filter((_, i) => i !== index);

    stateFunc(newAllegations.length > 0 ? newAllegations : []);
  }
};

export const pleadCollectionChange = (
  index: number,
  newText: string,
  arr: CollectionItem[],
  stateFunc: React.Dispatch<React.SetStateAction<CollectionItem[] | null>>,
) => {
  if (arr !== null) {
    const newCollection = arr.map((item, i) =>
      i === index ? { ...item, text: newText } : item,
    );
    stateFunc(newCollection);
  }
};

export const pleadCollectionAdd = (
  arr: CollectionItem[] | null,
  stateFunc: React.Dispatch<React.SetStateAction<CollectionItem[] | null>>,
) => {
  if (arr && arr !== null) {
    const lastItem = arr[arr.length - 1];
    const newNumber = lastItem?.number !== undefined ? lastItem.number + 1 : 1;
    const newItem: CollectionItem = { number: newNumber, text: "" };
    stateFunc([...arr, newItem]);
  } else {
    stateFunc([{ number: 1, text: "" }]);
  }
};

// Simple format the name of the generic info entry's name to remove underscore
export const formatGenericInfoName = (key: string) => {
  return key
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

// NOTE: Revisit this functionality if needed for generic info
export const genericInfoHandleAdd = (
  obj: { [key: string]: string } | null | undefined,
  setState: React.Dispatch<
    React.SetStateAction<{ [key: string]: string } | null | undefined>
  >,
) => {
  if (obj) {
    const newKey = `New_Detail_${Object.keys(obj).length + 1}`;
    setState({ ...obj, [newKey]: "" });
  } else {
    setState({ New_Detail_1: "" });
  }
};

export const genericInfoHandleRemove = (
  key: string,
  obj: { [key: string]: string } | null | undefined,
  setState: React.Dispatch<
    React.SetStateAction<{ [key: string]: string } | null | undefined>
  >,
) => {
  if (obj) {
    const { [key]: _, ...rest } = obj;
    setState(Object.keys(rest).length > 0 ? rest : null);
  }
};

export const genericInfoHandleChange = (
  key: string,
  newValue: string,
  obj: { [key: string]: string } | null | undefined,
  setState: React.Dispatch<
    React.SetStateAction<{ [key: string]: string } | null | undefined>
  >,
) => {
  if (obj) {
    setState({ ...obj, [key]: newValue });
  }
};

export const shouldShowOrDisable = (
  caseInfo: CaseResponse | undefined,
  type:
    | "disableVenue"
    | "disableJx"
    | "showReadOnly"
    | "disableCaseNum"
    | "disableJudge",
  showEdit: boolean,
  isPleading = false,
) => {
  const hasCaseId = caseInfo && !!caseInfo.id;
  const hasValidVenue =
    caseInfo && !!(caseInfo.venue && caseInfo.venue !== blankId);
  const hasValidJurisdiction =
    caseInfo && !!(caseInfo.jurisdiction && caseInfo.jurisdiction !== blankId);
  const hasValidCaseNum =
    !!caseInfo?.caseNumber && caseInfo.caseNumber !== undefined;
  const hasValidJudge = !!caseInfo?.judge && caseInfo.judge !== undefined;
  const hasCaseNumber = caseInfo && !!caseInfo.caseNumber;

  switch (type) {
    case "disableVenue":
      return (!showEdit || hasCaseId) && !!hasValidVenue;
    case "disableJx":
      return (!showEdit || hasCaseId) && !!hasValidJurisdiction;
    case "disableCaseNum":
      return hasValidCaseNum;
    case "disableJudge":
      return hasValidJudge;
    case "showReadOnly":
      if (isPleading) {
        return !showEdit && hasValidVenue && hasValidJurisdiction;
      }
      return (
        !showEdit && hasValidVenue && hasValidJurisdiction && hasCaseNumber
      );
    default:
      return false;
  }
};

export const getVenueValue = (
  _case: CaseResponse | undefined,
  venue: string,
) => {
  if (!_case?.venue || _case.venue === "" || _case.venue === blankId) {
    return venue;
  }
  return _case.venue;
};

export const getJxValue = (jurisdiction: JurisdictionResponse | undefined) => {
  if (
    !jurisdiction?.id ||
    jurisdiction.id === "" ||
    jurisdiction.id === blankId
  ) {
    return "";
  }
  return jurisdiction.id;
};

export const createFuzzySearch = <T,>(
  data: T[],
  options: IFuseOptions<T>,
  excludeWords: string[],
  query: string | null | undefined,
) => {
  if (!data) return;
  const fuse = new Fuse<T>(data, options);

  // Split on comma and take only the first part (before the comma)
  const queryBeforeComma = query?.split(",")[0]?.trim() || query;
  const cleanQuery = queryBeforeComma
    ?.toLowerCase()
    .split(/[\s-]+/)
    .filter((word) => !excludeWords.includes(word.toLowerCase()))
    .join(" ");
  const results = fuse.search(cleanQuery || "");

  if (results.length === 0) {
    return [];
  }

  return results;
};

export const sortClientsBySearchResults = <T extends ClientResponse>(
  allClients: ClientResponse[],
  fuzzyResults: FuseResult<T>[] | undefined,
): ClientResponse[] => {
  // No fuzzy search results? Return the client list like normal.
  if (!fuzzyResults || fuzzyResults.length === 0) {
    return allClients;
  }

  // If fuzzy search results are available, sort the 'allClients' data
  // by the scoring results provided by the fuzzy search results
  const scoreMap = new Map<string, number>();
  for (const result of fuzzyResults) {
    if (result.item.name) {
      scoreMap.set(result.item.name.toLowerCase(), result.score || 0);
    }
  }

  return [...allClients].sort((a, b) => {
    const aName = a.name?.toLowerCase();
    const bName = b.name?.toLowerCase();

    const aScore = aName ? scoreMap.get(aName) : undefined;
    const bScore = bName ? scoreMap.get(bName) : undefined;

    if (aScore !== undefined && bScore !== undefined) {
      return aScore - bScore;
    }

    if (aScore !== undefined) return -1;
    if (bScore !== undefined) return 1;

    return 0;
  });
};
